import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120pcSchIService } from './f1120pc-sch-i.service';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120pc-sch-i',
    templateUrl: './f1120pc-sch-i.component.html',
    styleUrls: ['./f1120pc-sch-i.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120pcSchIComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;

  // Modal properties
  modalTitle = "Schedule I Other Information (see instructions)";
  showFormLoader = true;
  crudLoading = false;

  // Tab management
  activeTab = 1;
  tabs = [
    { id: 1, name: 'Form Entry' },
    { id: 2, name: 'Form View' },
    { id: 3, name: 'Audit' },
    { id: 4, name: 'GOLD', disabled: true }
  ];

  // Entity data properties
  entityList: any[] = [];
  leId: any;
  cdr_no: any;
  leType: any;
  formName = '1120PC';
  hgt = '96%';
  systemFlag = 'D';

  // Form data
  scheduleIFormView: any = { rows: [] };
  scheduleIFormView1: any[] = [];
  scheduleIFormView2: any[] = [];
  formListOriginal: any = {};
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };

  // Batch data for Audit tab
  BatchList: any[] = [];
  BatchListData: any[] = [];
  batchLoader = false;
  showBatchData = false;
  showRequestLoader = true;
  prev_index: any = null;

  // GOLD data
  GoldData: any[] = [];

  // Flag to prevent multiple initialization
  private isInitialized = false;

  rowData: any = {};
  filterParams: any = {};
  baseUrls: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120pcSchIService: F1120pcSchIService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseUrls = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.isInitialized = false;
    this.cdr.markForCheck();
  }

  init() {
    try {
      console.log('init() called', {
        hasGlobalService: !!this.globalService,
        hasGENERAL_CONFIG: !!this.GENERAL_CONFIG,
        hasRowData: !!(this.rowData && Object.keys(this.rowData).length > 0),
        hasUSER_SETTINGS: !!this.USER_SETTINGS,
        isInitialized: this.isInitialized
      });

      if (!this.globalService || !this.GENERAL_CONFIG || !this.USER_SETTINGS) {
        console.log('Missing required data, skipping init');
        return;
      }

      if (this.rowData && Object.keys(this.rowData).length > 0 && !this.isInitialized) {
        console.log('Initializing F1120PC-SCH-I component');
        this.isInitialized = true;
        this.setupEntityData();
        this.fetchSchIrecords();
        this.fetchSchIimports();
        this.fetchGoldData();
      }
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
    }
  }

  private setupEntityData() {
    // CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;

    // MANDATORY ORDER: Tax Year, LEID, LE Name, LE Type, ME
    this.entityList = [
      { key: "Tax Year", value: this.rowData.TAX_YEAR },
      { key: "LEID", value: this.rowData.LEID },
      { key: "LE Name", value: this.rowData.LE_NAME },
      { key: "LE Type", value: this.rowData.LE_TYPE },
      { key: "ME", value: this.rowData.ME }
    ];
  }

  fetchSchIrecords() {
    const params = {
      "action_id": 603,
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY
    };

    console.log('fetchSchIrecords called with params:', params);

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      console.log('Form data received:', data);
      this.processFormData(data);
    }, (error: any) => {
      console.error('Error loading form data:', error);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    });
  }

  fetchSchIimports() {
    const params = {
      "client_key": this.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120PC Sch I",
      "status": 'Y'
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=wwledr',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.BatchList = data.jsonObject || [];
      this.BatchList.forEach((value: any, key: number) => {
        this.BatchList[key].TOGGLE = false;
      });
      this.showRequestLoader = false;
      this.cdr.detectChanges();
    }, (error: any) => {
      console.error('Error loading batch data:', error);
      this.showRequestLoader = false;
      this.BatchList = [];
      this.cdr.detectChanges();
    });
  }

  fetchGoldData() {
    const params = {
      "client_key": this.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120PC Sch I",
      "status": 'Y'
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=n887j5',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.GoldData = data.jsonObject || [];
      this.cdr.detectChanges();
    }, (error: any) => {
      console.error('Error loading GOLD data:', error);
      this.GoldData = [];
      this.cdr.detectChanges();
    });
  }

  private processFormData(data: any) {
    console.log('processFormData called, data.irsForm.formItemList.length:', data.irsForm?.formItemList?.length);
    this.scheduleIFormView.rows = [];
    var saveParamDtls: any[] = [];
    var tempDataObj: any = {};

    for (var i = 1; i < data.irsForm.formItemList.length; i++) {
      saveParamDtls = [];
      tempDataObj = {};
      saveParamDtls = data.irsForm.formItemList[i].dataRowList[0].rowDimensions.object_id.split('~');

      for (var j = 0; j < data.irsForm.formItemList[i].dataRowList[0].dataCellList.length; j++) {
        if (j == 0) {
          tempDataObj.num = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].name;
        } else if (j == 1) {
          tempDataObj.description = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].name;
        } else if (j == 2) {
          tempDataObj.lineAttrKey = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order_a = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[2];
          
          const cellValue = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
          
          if (cellValue == null) {
            // NULL = 0 (same as AngularJS line 59)
            tempDataObj.value = 0;
            if (tempDataObj.num == "1c" || tempDataObj.num == "4" || tempDataObj.num == "5" || tempDataObj.num == "16") {
              tempDataObj.conditionalField = true;
            } else {
              tempDataObj.conditionalField = false;
            }
          } else if (typeof cellValue === 'string' && cellValue.toLowerCase() === "true") {
            // "true" = true for checkboxes (same as AngularJS line 65)
            tempDataObj.value = true;
            if (tempDataObj.num == "1c" || tempDataObj.num == "4" || tempDataObj.num == "5" || tempDataObj.num == "16") {
              tempDataObj.conditionalField = false;
            }
          } else if (typeof cellValue === 'string' && cellValue.toLowerCase() === "false") {
            // "false" = false for checkboxes (same as AngularJS line 70)
            tempDataObj.value = false;
            if (tempDataObj.num == "1c" || tempDataObj.num == "4" || tempDataObj.num == "5" || tempDataObj.num == "16") {
              tempDataObj.conditionalField = true;
            }
          } else if (cellValue == 'NA' || cellValue === 'na') {
            // NA value (same as AngularJS line 75)
            tempDataObj.value = 'NA';
            if (tempDataObj.num == "1c" || tempDataObj.num == "4" || tempDataObj.num == "5" || tempDataObj.num == "16") {
              tempDataObj.conditionalField = true;
            } else {
              tempDataObj.conditionalField = false;
            }
          } else {
            // For text fields and numbers, keep original value (same as AngularJS line 82)
            tempDataObj.value = cellValue;
          }
        } else if (j == 3) {
          tempDataObj.lineAttrKey2 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name2 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order_b = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[2];
          tempDataObj.value_user_input = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
        } else if (j == 4) {
          tempDataObj.lineAttrKey3 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name3 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order_c = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[2];
          tempDataObj.value_user_input3 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
        } else {
          tempDataObj.lineAttrKey4 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name4 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order_d = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[2];
          tempDataObj.value_user_input4 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
        }
      }
      
      tempDataObj.line_order = saveParamDtls[8];
      tempDataObj.saveParams = saveParamDtls;
      this.scheduleIFormView.rows.push(tempDataObj);
    }

    // Apply conditional field logic after loading data (from AngularJS lines 106-115)
    if (this.scheduleIFormView.rows[7] && this.scheduleIFormView.rows[7].value == true) {
      this.toggleConditionalField([8, 9, 10], false);
    } else {
      this.toggleConditionalField([8, 9, 10], true);
    }
    
    if (this.scheduleIFormView.rows[13] && this.scheduleIFormView.rows[13].value == true) {
      this.toggleConditionalField([14, 15, 16], false);
    } else {
      this.toggleConditionalField([14, 15, 16], true);
    }

    this.formListOriginal = _.cloneDeep(this.scheduleIFormView);
    this.initFormView(this.scheduleIFormView);
    
    // CRITICAL: Turn off loader AFTER all data is processed
    this.showFormLoader = false;
    
    console.log('Form data loaded. scheduleIFormView1 length:', this.scheduleIFormView1.length);
    console.log('Form data loaded. scheduleIFormView2 length:', this.scheduleIFormView2.length);
    console.log('showFormLoader set to FALSE:', this.showFormLoader);
    
    // Force change detection
    this.cdr.detectChanges();
  }

  toggleConditionalField(arr: number[], flag: boolean) {
    arr.forEach((value: number) => {
      if (this.scheduleIFormView.rows[value]) {
        this.scheduleIFormView.rows[value].conditionalField = flag;
        if (flag) {
          this.scheduleIFormView.rows[value].value = "";
        }
      }
    });
  }

  loadTransactions(index: number, trans_key: string) {
    this.batchLoader = true;
    const params = {
      "client_key": this.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120PC Sch I",
      "transaction_key": trans_key
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=l6gzle',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      var tempData = data.jsonObject || [];
      var currLineNo: any;
      var tempBatchList: any = {};
      var tempBatchData: any[] = [];
      
      tempData.forEach((value: any, key: number) => {
        if (key == 0) {
          currLineNo = tempData[key].LINE_NO;
          tempBatchList = tempData[key];
        } else {
          if (tempData[key].LINE_NO == currLineNo) {
            tempBatchList.ATTRIBUTE_VALUE_B = tempData[key].ATTRIBUTE_VALUE;
            tempBatchData.push(tempBatchList);
            tempBatchList = {};
          } else {
            currLineNo = tempData[key].LINE_NO;
            tempBatchData.push(tempBatchList);
            tempBatchList = {};
            tempBatchList = tempData[key];
          }
        }
      });
      
      this.batchLoader = false;
      this.showBatchData = true;
      this.BatchListData = tempBatchData;
      this.cdr.detectChanges();
    }, (error: any) => {
      console.error('Error loading transaction data:', error);
      this.batchLoader = false;
      this.showBatchData = false;
      this.cdr.detectChanges();
    });
  }

  showUploads(index: number, trans_key: string) {
    if (this.prev_index !== null) {
      if (this.prev_index !== index) {
        this.BatchList[this.prev_index].TOGGLE = false;
        this.prev_index = index;
        this.showBatchData = false;
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.loadTransactions(index, trans_key);
      } else {
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.showBatchData = true;
      }
    } else {
      this.prev_index = index;
      this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
      this.loadTransactions(index, trans_key);
    }
  }

  changeTab(tabId: number) {
    // If leaving GOLD tab (4), disable it again
    if (this.activeTab === 4 && tabId !== 4) {
      this.tabs[3].disabled = true;
    }
    
    this.activeTab = tabId;
    this.cdr.detectChanges();
  }

  goldNavigation() {
    console.log('goldNavigation called, enabling GOLD tab');
    this.activeTab = 4;
    this.tabs[3].disabled = false;
  }

  // Handler for the mutually-exclusive checkbox groups (1a/1b/1c and 2a/2b).
  // Uses (change) instead of (click) so the new checked state is read from the
  // DOM event AFTER the browser has toggled the checkbox, avoiding the timing
  // issue where (click) fires before [(ngModel)] updates row.value.
  onGroupCheckboxChange(index: number, row: any, event: Event) {
    row.value = (event.target as HTMLInputElement).checked;
    this.fieldVisibility(index, row);
    this.cdr.markForCheck();
  }

  fieldVisibility(index: number, row: any, name?: string) {
    const state = row.value;

    if (index <= 6) {
      if (state && index == 1) {
        this.scheduleIFormView.rows[2].value = false;
        this.scheduleIFormView.rows[3].value = false;
        this.scheduleIFormView.rows[3].conditionalField = true;
      } else if (state && index == 2) {
        this.scheduleIFormView.rows[1].value = false;
        this.scheduleIFormView.rows[3].value = false;
        this.scheduleIFormView.rows[3].conditionalField = true;
      } else if (state && index == 3) {
        this.scheduleIFormView.rows[1].value = false;
        this.scheduleIFormView.rows[2].value = false;
        this.scheduleIFormView.rows[3].conditionalField = !this.scheduleIFormView.rows[3].conditionalField;
      } else if (state && index == 5) {
        this.scheduleIFormView.rows[6].value = false;
      } else if (state && index == 6) {
        this.scheduleIFormView.rows[5].value = false;
      } else {
        if (index == 3) {
          this.scheduleIFormView.rows[3].conditionalField = !this.scheduleIFormView.rows[3].conditionalField;
        }
      }
    } else if (index == 7) {
      this.checkBoxToggle(index, name, state, [8, 9, 10], "value", "attrib_name");
    } else if (index == 11) {
      this.checkBoxToggle(index, name, state, [index], "value_user_input", "attrib_name2");
      this.scheduleIFormView.rows[index].value_user_input3 = "";
      this.scheduleIFormView.rows[index].value_user_input4 = "";
    } else if (index == 12) {
      this.checkBoxToggle(index, name, state, [index], "value_user_input", "attrib_name2");
    } else if (index == 13) {
      this.checkBoxToggle(index, name, state, [14, 15, 16], "value", "attrib_name");
    } else if (index == 36) {
      this.checkBoxToggle(index, name, state, [index], "value_user_input", "attrib_name2");
    } else {
      // For other rows, toggle conditional field and handle NA state
      this.scheduleIFormView.rows[index].conditionalField = !this.scheduleIFormView.rows[index].conditionalField;
      if ((name == 'yes' && state !== true) || (name == 'no' && state !== false)) {
        this.scheduleIFormView.rows[index].value = 'NA';
      }
    }
  }

  checkBoxToggle(index: number, name: string | undefined, state: boolean, arr: number[], val: string, attr: string) {
    // checkBoxToggle logic from AngularJS (lines 262-273)
    // CRITICAL: Fixed condition - when clicking "yes" and state is NOT true, or clicking "no" and state is NOT false
    if ((name == 'yes' && state !== true) || (name == 'no' && state !== false)) {
      this.scheduleIFormView.rows[index].value = 'NA';
      if (name == 'yes' && state !== true) {
        arr.forEach((value: number) => {
          this.scheduleIFormView.rows[value].conditionalField = true;
        });
      }
    } else {
      this.toggleCondition(arr, state, val, attr);
    }
  }

  toggleCondition(index: number[], state: boolean, type: string, attr: string) {
    index.forEach((value: number) => {
      if ((this.scheduleIFormView.rows[value].conditionalField && !state) || 
          (!this.scheduleIFormView.rows[value].conditionalField && state)) {
        return;
      } else {
        this.scheduleIFormView.rows[value].conditionalField = !this.scheduleIFormView.rows[value].conditionalField;
      }
    });
    
    if (state) {
      index.forEach((value: number) => {
        this.scheduleIFormView.rows[value][type] = this.GoldData[0] ? this.GoldData[0][this.scheduleIFormView.rows[value][attr]] : '';
      });
    } else {
      index.forEach((value: number) => {
        this.scheduleIFormView.rows[value][type] = "";
      });
    }
  }

  onYesNoChange(index: number, value: boolean, row: any) {
    row.value = value;
    this.fieldVisibility(index, row);
  }

  // CRITICAL: Proper Yes/No checkbox handler (matching AngularJS behavior)
  onYesNoCheckboxClick(index: number, row: any, clickedOption: string) {
    // Get current state BEFORE click (because click event fires before change)
    const currentValue = row.value;
    
    if (clickedOption === 'yes') {
      // Clicking "Yes" checkbox
      if (currentValue === true) {
        // Already selected "Yes", uncheck it -> set to NA
        row.value = 'NA';
      } else {
        // Not selected "Yes", select it
        row.value = true;
      }
    } else {
      // Clicking "No" checkbox
      if (currentValue === false) {
        // Already selected "No", uncheck it -> set to NA
        row.value = 'NA';
      } else {
        // Not selected "No", select it
        row.value = false;
      }
    }
    
    // Call field visibility logic with the NEW value
    this.fieldVisibility(index, row, clickedOption);
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    var formRowDataA: any = {};
    var formRowDataB: any = {};
    var formRowDataC: any = {};
    var formRowDataD: any = {};
    var tempArray: any[] = [];

    for (var i = 0; i < this.scheduleIFormView.rows.length; i++) {
      formRowDataA = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: this.scheduleIFormView.rows[i].saveParams[1],
        line_order: this.scheduleIFormView.rows[i].line_order,
        attrib_order: this.scheduleIFormView.rows[i].attrib_order_a,
        line_description: this.scheduleIFormView.rows[i].description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: this.scheduleIFormView.rows[i].saveParams[2],
        line_no: this.scheduleIFormView.rows[i].num,
        occurence: this.scheduleIFormView.rows[i].saveParams[7],
        line_attrib_key: this.scheduleIFormView.rows[i].lineAttrKey,
        Attribute_Name: this.scheduleIFormView.rows[i].attrib_name,
        Attribute_Value: this.scheduleIFormView.rows[i].value ? this.scheduleIFormView.rows[i].value.toString() : this.scheduleIFormView.rows[i].value === false ? this.scheduleIFormView.rows[i].value.toString() : '',
        trans_dtls_key: this.scheduleIFormView.rows[i].saveParams[3],
        trans_status: this.scheduleIFormView.rows[i].saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowDataA);
      
      if (typeof this.scheduleIFormView.rows[i].value_user_input !== "undefined") {
        formRowDataB = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: this.scheduleIFormView.rows[i].saveParams[1],
          line_order: this.scheduleIFormView.rows[i].line_order,
          attrib_order: this.scheduleIFormView.rows[i].attrib_order_b,
          line_description: this.scheduleIFormView.rows[i].description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: this.scheduleIFormView.rows[i].saveParams[2],
          line_no: this.scheduleIFormView.rows[i].num,
          occurence: this.scheduleIFormView.rows[i].saveParams[7],
          line_attrib_key: this.scheduleIFormView.rows[i].lineAttrKey2,
          Attribute_Name: this.scheduleIFormView.rows[i].attrib_name2,
          Attribute_Value: this.scheduleIFormView.rows[i].value_user_input ? this.scheduleIFormView.rows[i].value_user_input.toString() : '0',
          trans_dtls_key: this.scheduleIFormView.rows[i].saveParams[3],
          trans_status: this.scheduleIFormView.rows[i].saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataB);
      }
      
      if (typeof this.scheduleIFormView.rows[i].value_user_input3 !== "undefined") {
        formRowDataC = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: this.scheduleIFormView.rows[i].saveParams[1],
          line_order: this.scheduleIFormView.rows[i].line_order,
          attrib_order: this.scheduleIFormView.rows[i].attrib_order_c,
          line_description: this.scheduleIFormView.rows[i].description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: this.scheduleIFormView.rows[i].saveParams[2],
          line_no: this.scheduleIFormView.rows[i].num,
          occurence: this.scheduleIFormView.rows[i].saveParams[7],
          line_attrib_key: this.scheduleIFormView.rows[i].lineAttrKey3,
          Attribute_Name: this.scheduleIFormView.rows[i].attrib_name3,
          Attribute_Value: this.scheduleIFormView.rows[i].value_user_input3 ? this.scheduleIFormView.rows[i].value_user_input3.toString() : '',
          trans_dtls_key: this.scheduleIFormView.rows[i].saveParams[3],
          trans_status: this.scheduleIFormView.rows[i].saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataC);
      }
      
      if (typeof this.scheduleIFormView.rows[i].value_user_input4 !== "undefined") {
        formRowDataD = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: this.scheduleIFormView.rows[i].saveParams[1],
          line_order: this.scheduleIFormView.rows[i].line_order,
          attrib_order: this.scheduleIFormView.rows[i].attrib_order_d,
          line_description: this.scheduleIFormView.rows[i].description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: this.scheduleIFormView.rows[i].saveParams[2],
          line_no: this.scheduleIFormView.rows[i].num,
          occurence: this.scheduleIFormView.rows[i].saveParams[7],
          line_attrib_key: this.scheduleIFormView.rows[i].lineAttrKey4,
          Attribute_Name: this.scheduleIFormView.rows[i].attrib_name4,
          Attribute_Value: this.scheduleIFormView.rows[i].value_user_input4 ? this.scheduleIFormView.rows[i].value_user_input4.toString() : '',
          trans_dtls_key: this.scheduleIFormView.rows[i].saveParams[3],
          trans_status: this.scheduleIFormView.rows[i].saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataD);
      }
    }
    
    this.formData.editrows.push(tempArray);

    const mainData: any[] = [];
    mainData.push(this.formData);

    this.f1120pcSchIService.saveF1120pcSchIForm(
      this.GENERAL_CONFIG.base_url,
      mainData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });

          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    this.scheduleIFormView = _.cloneDeep(this.formListOriginal);
    this.scheduleIFormView1 = _.cloneDeep(this.formListOriginal.rows.slice(0, 20));
    this.scheduleIFormView2 = _.cloneDeep(this.formListOriginal.rows.slice(20));
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  initFormView(filteredFormData: any) {
    this.scheduleIFormView = filteredFormData;
    this.scheduleIFormView1 = this.scheduleIFormView.rows.slice(0, 20);
    this.scheduleIFormView2 = this.scheduleIFormView.rows.slice(20);
    console.log('initFormView called. Total rows:', this.scheduleIFormView.rows.length);
    console.log('scheduleIFormView1:', this.scheduleIFormView1.length);
    console.log('scheduleIFormView2:', this.scheduleIFormView2.length);
  }

  // TrackBy function for performance optimization
  itemTracker(index: number, item: any): any {
    return item.num || index;
  }
}

